import React, { useMemo } from 'react';
import { Tooltip } from 'antd';
import { StatusCalculationEngine, StatusCalculationParams, StatusResult, StatusEnum } from '../services/StatusCalculationEngine';

interface StatusIndicatorProps {
  actualValue: number;
  referenceRange: string;
  elementName: string;
  cityLevel: number;
  evaluation?: string;
  showAnimation?: boolean;
  showTooltip?: boolean;
  size?: 'small' | 'medium' | 'large';
}

/**
 * 解析参考范围字符串，提取最小值和最大值
 */
const parseReferenceRange = (referenceRange: string): { min?: number; max?: number } => {
  if (!referenceRange || referenceRange === '-') {
    return {};
  }

  // 处理各种格式的参考范围
  // 格式1: "100-200" 或 "100 - 200"
  const rangeMatch = referenceRange.match(/(\d+(?:\.\d+)?)\s*[-~～]\s*(\d+(?:\.\d+)?)/);
  if (rangeMatch) {
    return {
      min: parseFloat(rangeMatch[1]),
      max: parseFloat(rangeMatch[2])
    };
  }

  // 格式2: "≤100" 或 "<=100"
  const maxOnlyMatch = referenceRange.match(/[≤<=]\s*(\d+(?:\.\d+)?)/);
  if (maxOnlyMatch) {
    return {
      max: parseFloat(maxOnlyMatch[1])
    };
  }

  // 格式3: "≥100" 或 ">=100"
  const minOnlyMatch = referenceRange.match(/[≥>=]\s*(\d+(?:\.\d+)?)/);
  if (minOnlyMatch) {
    return {
      min: parseFloat(minOnlyMatch[1])
    };
  }

  // 格式4: 纯数字，视为最大值
  const numberMatch = referenceRange.match(/^\d+(?:\.\d+)?$/);
  if (numberMatch) {
    return {
      max: parseFloat(referenceRange)
    };
  }

  return {};
};

const StatusIndicator: React.FC<StatusIndicatorProps> = ({
  actualValue,
  referenceRange,
  elementName,
  cityLevel,
  evaluation,
  showAnimation = true,
  showTooltip = true,
  size = 'medium'
}) => {
  // 计算状态
  const statusResult: StatusResult = useMemo(() => {
    try {
      const { min, max } = parseReferenceRange(referenceRange);

      const params: StatusCalculationParams = {
        actualValue,
        referenceMin: min,
        referenceMax: max,
        elementName,
        cityLevel
      };

      return StatusCalculationEngine.calculate(params);
    } catch (error) {
      console.error('状态计算失败:', error);
      return {
        status: StatusEnum.NORMAL,
        color: '#999',
        icon: '?',
        text: '计算异常',
        description: '无法计算状态',
        deviationPercentage: undefined
      };
    }
  }, [actualValue, referenceRange, elementName, cityLevel]);

  // 根据尺寸设置样式
  const sizeStyles = {
    small: {
      fontSize: '12px',
      iconSize: '14px',
      padding: '2px 6px'
    },
    medium: {
      fontSize: '14px',
      iconSize: '16px',
      padding: '4px 8px'
    },
    large: {
      fontSize: '16px',
      iconSize: '18px',
      padding: '6px 12px'
    }
  };

  const currentSizeStyle = sizeStyles[size];

  // 状态指示器样式
  const indicatorStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    color: statusResult.color,
    fontWeight: 'bold',
    fontSize: currentSizeStyle.fontSize,
    padding: currentSizeStyle.padding,
    borderRadius: '4px',
    border: `1px solid ${statusResult.color}20`,
    background: `${statusResult.color}08`,
    cursor: showTooltip ? 'pointer' : 'default',
    transition: showAnimation ? 'all 0.3s ease' : 'none',
    ...(showAnimation && {
      animation: statusResult.status !== StatusEnum.NORMAL ? 'pulse 2s infinite' : 'none'
    })
  };

  // 图标样式
  const iconStyle: React.CSSProperties = {
    fontSize: currentSizeStyle.iconSize,
    lineHeight: 1,
    fontWeight: 'bold'
  };

  // 生成详细的 Tooltip 内容
  const tooltipContent = useMemo(() => {
    const details = [
      `检查结果: ${actualValue}`,
      `参考范围: ${referenceRange || '无'}`,
      `培训要素: ${elementName}`,
      `城市等级: ${cityLevel}级`,
      `状态评估: ${statusResult.description}`
    ];

    if (statusResult.deviationPercentage !== undefined) {
      details.push(`偏差幅度: ${Math.abs(statusResult.deviationPercentage).toFixed(2)}%`);
    }

    if (evaluation) {
      details.push(`评定信息: ${evaluation}`);
    }

    // 特殊说明
    if (elementName.includes('人均课时费')) {
      if (cityLevel <= 2) {
        details.push('计算规则: 一二线城市仅与参考最大值比较');
      } else {
        details.push('计算规则: 其他城市与参考范围比较');
      }
    } else {
      details.push('计算规则: 与参考范围比较');
    }

    return (
      <div style={{ maxWidth: '300px' }}>
        <div style={{ fontWeight: 'bold', marginBottom: '8px', color: statusResult.color }}>
          状态详情
        </div>
        {details.map((detail, index) => (
          <div key={index} style={{ marginBottom: '4px', fontSize: '12px' }}>
            {detail}
          </div>
        ))}
      </div>
    );
  }, [actualValue, referenceRange, elementName, cityLevel, statusResult, evaluation]);

  const statusElement = (
    <span style={indicatorStyle}>
      <span style={iconStyle}>{statusResult.icon}</span>
      <span>{statusResult.text}</span>
    </span>
  );

  if (showTooltip) {
    return (
      <Tooltip
        title={tooltipContent}
        placement="top"
        overlayStyle={{ maxWidth: '350px' }}
      >
        {statusElement}
      </Tooltip>
    );
  }

  return statusElement;
};

// 添加动画样式
const AnimationStyles = () => (
  <style>{`
    @keyframes pulse {
      0% {
        transform: scale(1);
        opacity: 1;
      }
      50% {
        transform: scale(1.05);
        opacity: 0.8;
      }
      100% {
        transform: scale(1);
        opacity: 1;
      }
    }

    .status-indicator-glow {
      box-shadow: 0 0 10px rgba(24, 144, 255, 0.3);
    }

    .status-indicator-danger {
      animation: pulse 1.5s infinite;
    }

    .status-indicator-warning {
      animation: pulse 2s infinite;
    }
  `}</style>
);

// 导出组件并附加动画样式
const StatusIndicatorWithStyles: React.FC<StatusIndicatorProps> = (props) => (
  <>
    <AnimationStyles />
    <StatusIndicator {...props} />
  </>
);

export default StatusIndicatorWithStyles;

// 也导出原始组件以供测试使用
export { StatusIndicator };

// 导出相关类型
export type { StatusIndicatorProps };
export { StatusCalculationEngine, StatusEnum };
export type { StatusResult };